// v2.19.26 — Page publique "Allumons la Côte d'Ivoire" (sans auth).
// Carte read-only + compteurs live + CTA + partage social. Mobile-first.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const CI_BOUNDS = [[-8.65, 4.30], [-2.45, 10.80]];
const LEVEL_COLORS = { cold: "#1a1a1a", warm: "#3d2817", medium: "#d97706", hot: "#fbbf24" };

const PAGE_URL = typeof window !== "undefined" ? `${window.location.origin}/carte-publique` : "";
const TWEET_TEXT = "🇨🇮 Allumons la Côte d'Ivoire avec AYA AI ! Réclame ton adresse virtuelle et fais briller ta sous-préfecture sur la carte nationale 🔥";

function ShareButtons({ pct }) {
  const url = encodeURIComponent(PAGE_URL);
  const txt = encodeURIComponent(`${TWEET_TEXT}\n${pct}% du territoire déjà illuminé.`);
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <a data-testid="share-twitter" target="_blank" rel="noopener noreferrer"
         href={`https://twitter.com/intent/tweet?text=${txt}&url=${url}`}
         style={btnShare("#1DA1F2")}>🐦 Twitter</a>
      <a data-testid="share-whatsapp" target="_blank" rel="noopener noreferrer"
         href={`https://wa.me/?text=${txt}%20${url}`}
         style={btnShare("#25D366")}>💬 WhatsApp</a>
      <a data-testid="share-facebook" target="_blank" rel="noopener noreferrer"
         href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
         style={btnShare("#1877F2")}>📘 Facebook</a>
    </div>
  );
}
function btnShare(bg) {
  return {
    padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    background: bg, color: "white", textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.15)", whiteSpace: "nowrap",
    transition: "transform 120ms ease",
  };
}

function StatBigNum({ value, label, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: "14px 16px",
      background: "rgba(0,0,0,0.35)", borderRadius: 12,
      border: `1px solid ${accent}55`, textAlign: "center",
    }}>
      <div style={{ fontSize: 32, fontWeight: 800, color: accent, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#CBD5E1", marginTop: 4, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

export default function PublicCoverageMap() {
  const navigate = useNavigate();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [summary, setSummary] = useState(null);
  const [geojson, setGeojson] = useState(null);
  const [lights, setLights] = useState(null);
  const [ciOutline, setCiOutline] = useState(null);

  const fetchSummary = async () => {
    try {
      const r = await fetch(`${API}/api/public/coverage-summary`);
      const j = await r.json();
      if (j.ok) setSummary(j);
    } catch (_) {}
  };
  const fetchGeo = async () => {
    try {
      const r = await fetch(`${API}/api/v3/addresses/coverage-geojson`);
      setGeojson(await r.json());
    } catch (_) {}
  };
  const fetchLights = async () => {
    try {
      const r = await fetch(`${API}/api/public/claims-lights`);
      setLights(await r.json());
    } catch (_) {}
  };
  const fetchCiOutline = async () => {
    try {
      const r = await fetch(`${API}/api/public/ci-outline`);
      setCiOutline(await r.json());
    } catch (_) {}
  };

  useEffect(() => {
    fetchSummary(); fetchGeo(); fetchLights(); fetchCiOutline();
    const iv = setInterval(() => { fetchSummary(); fetchGeo(); fetchLights(); }, 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    document.title = "🇨🇮 Allumons la Côte d'Ivoire — AYA AI";
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !MAPBOX_TOKEN) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      bounds: CI_BOUNDS,
      fitBoundsOptions: { padding: 30 },
      interactive: true,
      dragRotate: false,
    });
    mapRef.current = map;
    map.on("load", () => {
      // v2.19.37c — Style "Night Lights" : silhouette CI gold + heatmap doré + points individuels.
      // ─ 1. CI country border gold glow (source: gadm L1, leur union = silhouette CI)
      map.addSource("ci-outline", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      // Underlay halo large
      map.addLayer({
        id: "ci-border-glow-wide", type: "line", source: "ci-outline",
        paint: { "line-color": "#FFD700", "line-width": 5, "line-opacity": 0.18, "line-blur": 8 },
      });
      // Main gold line
      map.addLayer({
        id: "ci-border-glow", type: "line", source: "ci-outline",
        paint: { "line-color": "#FFD700", "line-width": 1.2, "line-opacity": 0.55, "line-blur": 2 },
      });

      // ─ 2. Souspref subtle grid (juste un hint du découpage administratif)
      map.addSource("public-cov", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "public-fill", type: "fill", source: "public-cov",
        paint: { "fill-color": "#000000", "fill-opacity": 0 }, // transparent, gardé pour mousemove tooltip
      });
      map.addLayer({
        id: "public-outline", type: "line", source: "public-cov",
        paint: {
          "line-color": "#2A3B5C",
          "line-width": 0.3,
          "line-opacity": 0.2,
        },
      });

      // ─ 3. Night-lights : heatmap doré (densité)
      map.addSource("claims-lights", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "lights-heatmap",
        type: "heatmap",
        source: "claims-lights",
        maxzoom: 14,
        paint: {
          "heatmap-weight": ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 0, 0.4, 10, 1.2],
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 5, 1.0, 12, 2.5],
          "heatmap-radius":    ["interpolate", ["linear"], ["zoom"], 5, 14, 10, 30, 14, 60],
          "heatmap-opacity":   ["interpolate", ["linear"], ["zoom"], 5, 0.85, 14, 0.55],
          "heatmap-color": [
            "interpolate", ["linear"], ["heatmap-density"],
            0,    "rgba(0,0,0,0)",
            0.15, "rgba(255,176,0,0.35)",
            0.35, "rgba(255,200,40,0.6)",
            0.6,  "rgba(255,224,102,0.85)",
            0.85, "rgba(255,245,200,0.95)",
            1,    "rgba(255,255,255,1)",
          ],
        },
      });

      // ─ 4. Circle points individuels (les "villes vues depuis l'espace")
      map.addLayer({
        id: "lights-circles",
        type: "circle",
        source: "claims-lights",
        minzoom: 6,
        paint: {
          "circle-color": "#FFE066",
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            6,  ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 1.5, 10, 3],
            10, ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 3,   10, 6],
            14, ["interpolate", ["linear"], ["coalesce", ["get", "popularity_score"], 1], 1, 5,   10, 10],
          ],
          "circle-blur": 0.6,
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.5, 10, 0.85, 14, 1.0],
          "circle-stroke-color": "#FFFFFF",
          "circle-stroke-width": 0.4,
          "circle-stroke-opacity": 0.6,
        },
      });
    });
    let popup = null;
    map.on("mousemove", "public-fill", (e) => {
      if (!e.features?.[0]) return;
      map.getCanvas().style.cursor = "pointer";
      const p = e.features[0].properties;
      if (!popup) popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
      popup.setLngLat(e.lngLat).setHTML(`
        <div style="font:600 12px system-ui;color:#F1F5F9;background:rgba(15,23,42,0.95);padding:6px 10px;border-radius:6px;">
          📍 ${p.souspref_name || "—"} · ${p.claimed || 0} adresse(s)
        </div>`).addTo(map);
    });
    map.on("mouseleave", "public-fill", () => { map.getCanvas().style.cursor = ""; if (popup) popup.remove(); });
    return () => { try { map.remove(); } catch (_) {} mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!geojson || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("public-cov");
      if (src) src.setData(geojson);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [geojson]);

  // v2.19.37c — Push night-lights (claims points) vers la map
  useEffect(() => {
    if (!lights || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("claims-lights");
      if (src) src.setData(lights);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [lights]);

  // v2.19.37c — Push CI outline (gold silhouette) vers la map
  useEffect(() => {
    if (!ciOutline || !mapRef.current) return;
    const m = mapRef.current;
    const apply = () => {
      const src = m.getSource("ci-outline");
      if (src) src.setData(ciOutline);
    };
    if (m.isStyleLoaded()) apply(); else m.once("load", apply);
  }, [ciOutline]);

  const pct = summary?.illumination_pct ?? 0;

  return (
    <div data-testid="public-coverage-page" style={{
      minHeight: "100vh", background: "#0F172A", color: "#F1F5F9", padding: 16,
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Hero */}
        <div style={{ textAlign: "center", padding: "24px 0 8px" }}>
          <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1.1, marginBottom: 8 }}>
            🇨🇮 Allumons la Côte d'Ivoire
          </div>
          <div style={{ fontSize: 14, color: "#94A3B8" }}>
            {summary
              ? <>
                  <strong style={{ color: "#fbbf24" }}>{summary.souspref_lit}</strong> / {summary.total_souspref} sous-préfectures illuminées
                  {" · "}
                  <strong style={{ color: "#fbbf24" }}>{summary.total_claimed}</strong> adresses revendiquées par les Ivoiriens d'AYA
                </>
              : "Chargement…"}
          </div>
        </div>

        {/* KPI big numbers */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatBigNum value={summary?.total_claimed ?? "—"} label="Adresses" accent="#fbbf24" />
          <StatBigNum value={`${pct}%`} label="Territoire" accent="#d97706" />
          <StatBigNum value={`${summary?.regions_lit ?? "—"}/33`} label="Régions" accent="#60A5FA" />
          <StatBigNum value={`${summary?.districts_lit ?? "—"}/14`} label="Districts" accent="#A78BFA" />
        </div>

        {/* Map */}
        <div ref={mapContainerRef} data-testid="public-map-container" style={{
          width: "100%", height: "min(65vh, 600px)",
          borderRadius: 14, overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
        }} />

        {/* Dernière activité */}
        {summary?.last_claim && (
          <div style={{
            padding: "10px 14px", borderRadius: 10,
            background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)",
            fontSize: 13, color: "#FDE68A", textAlign: "center",
          }}>
            ✨ Dernière adresse créée à <strong>{summary.last_claim.souspref_name}</strong> il y a {Math.floor(summary.last_claim.ago_minutes / 60) || "<1"} heure(s)
          </div>
        )}

        {/* CTA */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "16px 0 32px" }}>
          <button
            onClick={() => navigate("/mon-compte")}
            data-testid="public-cta-claim"
            style={{
              padding: "16px 32px", borderRadius: 14, fontSize: 17, fontWeight: 800,
              background: "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)",
              color: "#0F172A", border: 0, cursor: "pointer",
              boxShadow: "0 8px 24px rgba(251,191,36,0.4)",
            }}
          >✨ Réclame ton adresse AYA</button>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>Partage la mission :</div>
          <ShareButtons pct={pct} />
        </div>
      </div>
    </div>
  );
}
